import React, { createContext, useContext, useMemo } from 'react'
import { Row, Col } from 'rsuite'
import { isMobile } from 'react-device-detect'

// Context to share data between compound components
const VisualTopicSelectorContext = createContext(null)

const useVisualTopicSelector = () => {
  const context = useContext(VisualTopicSelectorContext)
  if (!context) {
    throw new Error('VisualTopicSelector compound components must be used within VisualTopicSelector')
  }
  return context
}

const getCurrentHotspot = ({ partsGroupedByView, selectedPart }) => {
  const firstHotspot = Object.keys(partsGroupedByView)[0]
  return Object.keys(partsGroupedByView).includes(selectedPart) ? selectedPart : firstHotspot
}

const getHotspotForCurrentView = ({ hotSpotMapping, selectedView }) => {
  return hotSpotMapping?.mapping?.[selectedView] || {}
}

/**
 * Base VisualTopicSelector component
 * Handles shared logic for hotspot selection and topic display
 */
const VisualTopicSelector = ({
  selectedView,
  onSelectView = () => {},
  hotSpotMapping,
  onHotSpotClick = () => {},
  onTabChange = () => {},
  partsGroupedByView,
  onTopicSelect = () => {},
  selectedPart,
  updateQueryParams = () => {},
  children,
  ...rest // Additional props to be passed to the context by the customer-specific components
}) => {
  const currentHotspot = useMemo(
    () => getCurrentHotspot({ partsGroupedByView, selectedPart }),
    [partsGroupedByView, selectedPart]
  )

  const hotspotForCurrentView = useMemo(
    () => getHotspotForCurrentView({ hotSpotMapping, selectedView }),
    [hotSpotMapping, selectedView]
  )

  const partTopics = useMemo(() => partsGroupedByView[currentHotspot], [partsGroupedByView, currentHotspot])

  const contextValue = {
    selectedView,
    onSelectView,
    hotSpotMapping,
    onHotSpotClick,
    onTabChange,
    partsGroupedByView,
    onTopicSelect,
    updateQueryParams,
    selectedPart,
    currentHotspot,
    getCurrentHotspot,
    hotspotForCurrentView,
    getHotspotForCurrentView,
    partTopics,
    isMobile,
    ...rest
  }

  return (
    <VisualTopicSelectorContext.Provider value={contextValue}>
      <Row>{children}</Row>
    </VisualTopicSelectorContext.Provider>
  )
}

/**
 * Hotspots Column - Left side with car image and hotspots
 * Must be implemented by customer-specific components
 */
const Hotspots = ({ children }) => {
  const context = useVisualTopicSelector()

  return (
    <Col xs={24} md={12} className="pdl0">
      {typeof children === 'function' ? children(context) : children}
    </Col>
  )
}

/**
 * Topics Column - Right side with topic list
 * Must be implemented by customer-specific components
 */
const Topics = ({ children }) => {
  const context = useVisualTopicSelector()

  return (
    <Col xs={24} mdOffset={1} md={10} className={context.isMobile && 'pdl0'}>
      {typeof children === 'function' ? children(context) : children}
    </Col>
  )
}

// Attach compound components
VisualTopicSelector.Hotspots = Hotspots
VisualTopicSelector.Topics = Topics

export default VisualTopicSelector
export { useVisualTopicSelector }
